import React from 'react';
import { PropTypes } from 'prop-types';
import { Trans } from 'react-i18next';
import { captureError } from 'sentry';

import {
  CardCVCElement,
  CardExpiryElement,
  CardNumberElement,
  injectStripe,
} from 'react-stripe-elements';
import { isEmpty } from 'utils/types';
import { Button } from '@ontruck/orc';
import Link from 'components/styled/link';
import LoadSpinner from 'components/LoadSpinner';
import LiteralCurrency from 'components/LiteralCurrency';
import TimeErrorModal from 'components/summary/TimeErrorModal';
import ErrorMessage from 'components/ErrorMessage';

import FieldContainer from 'refactored/features/soForm/steps/card/creditCard/Form/components/FieldContainer';

const errorLabels = {
  card_declined: 'declined',
  incorrect_number: 'card',
  invalid_number: 'card',
  incomplete_number: 'card',
  incomplete_expiry: 'expiration',
  invalid_expiry_year_past: 'expiration',
  invalid_expiry_month_past: 'expiration',
  expired_card: 'expiration',
  invalid_expiry_month: 'expiration',
  invalid_expiry_year: 'expiration',
  incomplete_cvc: 'ccv',
};

class CardForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: undefined,
    };
  }

  handleSubmit = async () => {
    this.setState({ loading: true, error: undefined });

    const { stripe, handleSubmitPay, paymentIntentSecret } = this.props;
    // alert('handdle submit');

    // const paymentIntent = 123;

    // handleSubmitPay({ paymentIntent });
    // return;

    try {
      const { paymentIntent, error } = await stripe.handleCardPayment(
        paymentIntentSecret,
        {}
      );

      if (!isEmpty(error)) {
        this.handleError(error);
        captureError(error);
        return;
      }

      if (!isEmpty(paymentIntent)) {
        handleSubmitPay({ paymentIntent });
      }
    } catch (e) {
      this.handleError(e);
      captureError(e);
      this.setState({ loading: false });
    }
  };

  handleError = (error) => {
    if (Object.keys(errorLabels).indexOf(error.code) >= 0) {
      this.setState({ error: errorLabels[error.code] });
    } else {
      if (
        error &&
        error.code === 'payment_intent_unexpected_state' &&
        error.payment_intent &&
        error.payment_intent.status &&
        error.payment_intent.status === 'succeeded'
      ) {
        const { handleSubmitPay } = this.props;
        handleSubmitPay({ paymentIntent: error.payment_intent });
        return;
      }
      this.setState({ error: 'other' });
    }
    this.setState({ loading: false });
  };

  handleFocus = () => {
    this.setState({ error: '' });
  };

  handleChange = () => {
    this.setState({ error: '' });
  };

  render() {
    const {
      billingEmail,
      t,
      fullPrice,
      isSubmitting,
      hasPastError,
      hasQuoteError,
      hasErrors,
    } = this.props;
    const { loading, error } = this.state;
    return (
      <form>
        <div className="card-container">
          <FieldContainer label={t('fields.card')} hasError={error === 'card'}>
            <CardNumberElement
              placeholder="_ _ _ _  _ _ _ _  _ _ _ _  _ _ _ _ "
              style={{ base: { fontSize: '16px' } }}
              onFocus={this.handleFocus}
              onChange={this.handleChange}
            />
          </FieldContainer>
          <div className="block">
            <FieldContainer
              label={t('fields.expiration')}
              hasError={error === 'expiration'}
              onFocus={this.handleFocus}
              onChange={this.handleChange}
            >
              <CardExpiryElement
                placeholder="_ _ /_ _ "
                style={{ base: { fontSize: '16px' } }}
              />
            </FieldContainer>
            <FieldContainer
              label={t('fields.ccv')}
              hasError={error === 'ccv'}
              onFocus={this.handleFocus}
              onChange={this.handleChange}
            >
              <CardCVCElement
                placeholder="_ _ _ "
                style={{ base: { fontSize: '16px' } }}
              />
            </FieldContainer>
          </div>
        </div>

        {!isEmpty(error) && <p className="error">{t(`errors.${error}`)}</p>}

        <p>{t('info')}</p>
        <p>
          <Trans
            t={t}
            i18nKey="change_payment_method_info"
            values={{ billingEmail }}
            components={[
              <Link
                href={`mailto:${billingEmail}`}
                text={billingEmail}
                noTextDecoration
                target="_blank"
              />,
            ]}
          />
        </p>

        {hasPastError && <TimeErrorModal t={t} />}

        {hasErrors || hasQuoteError ? (
          <ErrorMessage t={t} />
        ) : (
          <div className="summary">
            <div className="vat-value">
              {t('price_with_vat')}
              <div className="price">
                <LiteralCurrency value={fullPrice} />{' '}
              </div>
            </div>

            <Button
              text={
                loading || isSubmitting ? (
                  <LoadSpinner skin="inButton" />
                ) : (
                  t('pay_and_confirm')
                )
              }
              type="button"
              size="L"
              disabled={loading || isSubmitting}
              onClick={this.handleSubmit}
            />
          </div>
        )}
      </form>
    );
  }
}

CardForm.propTypes = {
  t: PropTypes.func,
  handleSubmitPay: PropTypes.func,
  stripe: PropTypes.func.isRequired,

  billingEmail: PropTypes.string,
  paymentIntentSecret: PropTypes.string,
  fullPrice: PropTypes.number,
  isSubmitting: PropTypes.bool,
  hasPastError: PropTypes.bool,
  hasQuoteError: PropTypes.bool,
  hasErrors: PropTypes.bool,
};

CardForm.defaultProps = {
  t: (t) => t,
  handleSubmitPay: () => ({}),
  billingEmail: '',
  paymentIntentSecret: '',
  fullPrice: 0,
  isSubmitting: false,
  hasPastError: false,
  hasQuoteError: false,
  hasErrors: false,
};

export default injectStripe(CardForm);
